<!-- Modal for creating and editing CompoundWaterMeters -->
<!-- Copy right - © 2025 tecera gmbh -->

<script>
    import { fly, fade } from "svelte/transition";
    import {
        all_compound_water_meters,
        free_water_meters,
        is_modal_open,
        modal_mode,
        selected_device,
        toast_message,
        toast_message_error,
    } from "../../stores.js";
    import { BASE_API_URL } from "../../config.js";
    import ModalInputField from "./form_parts/ModalInputField.svelte";
    import KoordinatenSetzen from "./form_parts/KoordinatenSetzen.svelte";
    import ToggleButton from "../buttons/ToggleButton.svelte";
    import {
        get_free_water_meters,
        get_compound_water_meters,
        handle_bad_response,
    } from "../../scripts/data.js";
    import ConfirmModal from "./ConfirmModal.svelte";
    import SegmentControl from "./form_parts/SegmentControl.svelte";
    import CompoundRoleSelection from "./form_parts/CompoundRoleSelection.svelte";
    import { find_main_meter } from "../../scripts/utils.js";

    export let modals;
    export let open_modal;

    $: is_open = open_modal === modals.COMPOUND_WATER_METER;

    const MANUFACTURERS = [
        { id: "Lobaro", name: "Trinkbrunnen allgemein"},
        { id: "Lobaro", name: "Sensus" },
        { id: "Kamstrup", name: "Kamstrup" },
        { id: "Landis+Gyr", name: "Landis+Gyr" },
        { id: "Dragino", name: "Dragino" },
    ];
    let selected_manufacturer = null; // selected manufacturer for the device

    const METER_TYPES = [
        { id: "mbus_water_meter", name: "Trinkbrunnen" },
        { id: "mbus_heat_meter", name: "Wärmezähler" },
    ];
    let selected_meter_type = null; // selected meter type for the device

    // data for fields
    let title;
    let device_name = null;
    let city = null;
    let ortsteil = null;
    let strasse = null;
    let hausnummer = null;
    let longitude = null;
    let latitude = null;
    let ergaenzung = null;
    let bemerkung_lage = null;
    let meter_offset = null;
    let lobaro_key = null;
    let water_meter_id_input = null; // manual meterid

    function clear_inputs() {
        device_name = null;
        city = null;
        ortsteil = null;
        strasse = null;
        hausnummer = null;
        longitude = null;
        latitude = null;
        ergaenzung = null;
        bemerkung_lage = null;
        meter_offset = null;
        lobaro_key = null;
        water_meter_id_input = null;
    }

    // modal input fields
    let device_name_field = {
        id: "device_name",
        placeholder: "Bezeichnung",
        label: "Bezeichnung",
        max_length: 64,
        error_message: "Bitte Bezeichnung eingeben.",
    };

    let city_field = {
        id: "city",
        placeholder: "Stadt",
        label: "Stadt",
        max_length: 64,
    };

    let ortsteil_field = {
        id: "ortsteil",
        placeholder: "Ortsteil",
        label: "Ortsteil",
        max_length: 64,
    };

    let strasse_field = {
        id: "strasse",
        placeholder: "Straße",
        label: "Straße",
        max_length: 64,
    };

    let hausnummer_field = {
        id: "hausnummer",
        placeholder: "Hausnummer",
        label: "Hausnummer",
        max_length: 10,
    };

    let meter_offset_field = {
        id: "meter_offset",
        label: "Zählerstandausgleich",
        placeholder: "Startwert für den Zähler in m³",
        max_length: 16,
        tooltip:
            "Dieser Ausgleichswert (in m³) wird zu künftigen Zählerständen hinzugefügt, um den tatsächlichen Verbrauch korrekt darzustellen.",
    };

    let lobaro_key_field = {
        id: "lobaro_key",
        label: "wMBus AES-Schlüssel",
        placeholder: "z.B. AAAAAAAAAAAAAAAA",
        max_length: 64,
        error_message:
            "Ein wMBus AES-Schlüssel wird zum Anlegen des Geräts benötigt (8, 16 oder 32 Zeichen).",
    };

    let water_meter_id_input_field = {
        id: "water_meter_id_input",
        label: "Zählernummer",
        placeholder: "Zählernummer",
        max_length: 32,
        tooltip:"Die Zählernummer muss die korrekte Funkadresse des Zählers sein.",
        error_message:
            "Eine Zählernummer wird zum Anlegen des Geräts benötigt (8 oder 11 Zeichen).",
    };

    let selected_water_meter; // the selected water meter
    let selected_compounds_multiple = []; // the selected water meter for multi selection
    let device_active = true; // deviceconfig active status. true by default, but changed to the value of the actual config object
    let confirm_open = false; // whether the confirm modal is open
    let confirm_title =
        "Sind Sie sicher, dass Sie dieses Gerät löschen möchten?"; // title for the confirm modal
    let setManCoords = false; // if false the coordinates are created automiatcally
    let can_close = false; // determines whether the modal can be closed
    let compound_mode = false; // creates comound water meter if true, single water meter on false
    let creation_main_compound = null; // the main meter for compound water meter
    let compound_main_meter = null;

    // Reset selections upon changing "Tab"
    $: if (compound_mode !== undefined) {
        selected_compounds_multiple = [];
        selected_water_meter = null;
        device_was_chosen = false;
    }

    // reactivity to open and close the modal with delay
    $: {
        if (is_open) {
            document.body.style.overflow = "hidden";
            document.body.classList.add("modal-open");
            setTimeout(() => {
                can_close = true; // 250ms delay to prevent closing the modal immediately
            }, 250);
        } else {
            setTimeout(() => {
                document.body.style.overflow = "auto";
                document.body.classList.remove("modal-open");
            }, 350); // enable scrolling after the modal is closed
        }
    }

    function close_modal() {
        if (can_close) {
            open_modal = modals.NONE; // reset the open modal to none
            $modal_mode = "closed";
            can_close = false;
            tried_submitting = false; // reset the tried submitting flag
            $is_modal_open = false; // reset the modal open state
        }
    }

    let is_button_disabled = false;

    function timeout_button() {
        is_button_disabled = true;
        setTimeout(() => {
            is_button_disabled = false;
        }, 500);
    }

    // get pre-filled data for the form
    function get_form_data() {
        if ($modal_mode === "create") {
            title = "Trinkbrunnen anlegen";
            clear_inputs();
            selected_water_meter = null;
            selected_compounds_multiple = [];
            device_active = true;
            setManCoords = false;
            selected_manufacturer = null;
            selected_meter_type = null;
            device_was_chosen = false;
        } else if ($modal_mode === "edit") {
            compound_main_meter = find_main_meter($selected_device);
            title = "Trinkbrunnen editieren";
            device_name = compound_main_meter.name;
            city = compound_main_meter.location;
            ortsteil = compound_main_meter.district;
            strasse = compound_main_meter.street;
            hausnummer = compound_main_meter.house_number;
            longitude = compound_main_meter.longitude;
            latitude = compound_main_meter.latitude;
            ergaenzung = compound_main_meter.comment;
            bemerkung_lage = compound_main_meter.comment_location;
            device_active = compound_main_meter.active === 1 ? true : false;
            setManCoords = false;
            device_was_chosen = false;
            let volume_channel = compound_main_meter.channels.find(
                (channel) => channel.measurand === "Zählerstand",
            );
            if (volume_channel) {
                meter_offset = volume_channel.offset;
            } else {
                meter_offset = null;
            }
        }
    }

    // reactivity to fetch correct data or clear inputs
    $: if (is_open) {
        get_form_data();
    }

    /**
     *
     * Funktion ermittelt die Koordinaten der genannten Addresse mit Hilfe von Nominatim von OSM
     * Falls Koordinaten nicht ermittelt werden können, werden null Werte zurückgegeben
     *
     */
    async function get_coords(address) {
        let url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${address}`;
        try {
            let response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Error fetching coordinates`);
            }
            let data = await response.json();
            latitude = data[0].lat;
            longitude = data[0].lon;
            return [latitude, longitude];
        } catch (error) {
            latitude = null;
            longitude = null;
            return [latitude, longitude];
        }
    }

    // calling the API and saving the device after making some checks
    async function save_device() {
        timeout_button();
        let url_extension = `/compound_water_meters/`;
        let request_url = BASE_API_URL + url_extension;

        // convert boolean to int to save it
        let device_active_int = device_active ? 1 : 0;
        if (!setManCoords) {
            await set_coords();
        }
        let post_data = {};

        post_data = {
            device: selected_water_meter ? selected_water_meter.id : null,
            meter_id: selected_water_meter
                ? selected_water_meter.alt_id
                : water_meter_id_input,
            name: device_name,
            location: city,
            district: ortsteil,
            street: strasse,
            house_number: hausnummer,
            longitude: parseFloat(longitude),
            latitude: parseFloat(latitude),
            comment: ergaenzung,
            comment_location: bemerkung_lage,
            active: device_active_int,
            wmbus_key: lobaro_key,
            manufacturer: selected_manufacturer
                ? selected_manufacturer.id
                : null,
            meter_type: selected_meter_type ? selected_meter_type.id : null,
            meter_offset: parseFloat(meter_offset),
        };

        try {
            const response = await fetch(request_url, {
                method: "POST",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(post_data),
            });

            if (!response.ok) {
                let errorData = await response.json();
                toast_message_error.set(" " + errorData.error);
                handle_bad_response(response);
                return false;
            }
            let data = await response.json();

            all_compound_water_meters.update((compounds) => [
                ...compounds,
                data.data,
            ]);
            $selected_device = data.data;
            close_modal();
            $toast_message = " Gerät erfolgreich angelegt";
            get_free_water_meters();
            return true;
        } catch (error) {
            return false;
        }
    }

    // updating the device when editing. Box or compound water meter
    async function update_device() {
        timeout_button();

        // Compound Water Meter
        let compound_id = $selected_device.id;
        if (!setManCoords) {
            await set_coords();
        }
        let device_active_int = device_active ? 1 : 0;
        let post_data = {};

        post_data = {
            id: compound_id,
            name: device_name,
            location: city,
            district: ortsteil,
            street: strasse,
            house_number: hausnummer,
            longitude: parseFloat(longitude),
            latitude: parseFloat(latitude),
            comment: ergaenzung,
            comment_location: bemerkung_lage,
            active: device_active_int,
            meter_offset: parseFloat(meter_offset),
        };

        let url_extension = `/compound_water_meters/${compound_id}/`;
        let request_url = BASE_API_URL + url_extension;
        try {
            const response = await fetch(request_url, {
                method: "PUT",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(post_data),
            });
            if (!response.ok) {
                handle_bad_response(response);
                return false;
            }
            let data = await response.json();

            let idToFind = data.data.id;
            let index = $all_compound_water_meters.findIndex(
                (device_config) => device_config.id == idToFind,
            );
            $all_compound_water_meters[index] = data.data;
            $selected_device = data.data;
            close_modal();
            $toast_message = " Gerät erfolgreich editiert";

            return true;
        } catch (error) {
            return false;
        }
    }

    // updating the device when editing. Box or compound water meter
    async function update_compound_water_meter() {
        timeout_button();

        // Compound Water Meter
        let compound_id = creation_main_compound
            ? creation_main_compound.id
            : null;
        if (!setManCoords) {
            await set_coords();
        }
        let device_active_int = device_active ? 1 : 0;
        let post_data = {};

        post_data = {
            id: creation_main_compound ? creation_main_compound.id : null,
            sub_compound_ids: selected_compounds_multiple
                .filter((compound) => compound.id !== creation_main_compound.id)
                .map((compound) => compound.id),
            name: device_name,
            location: city,
            district: ortsteil,
            street: strasse,
            house_number: hausnummer,
            longitude: parseFloat(longitude),
            latitude: parseFloat(latitude),
            comment: ergaenzung,
            comment_location: bemerkung_lage,
            active: device_active_int,
            meter_offset: parseFloat(meter_offset),
        };

        let url_extension = `/compound_water_meters/${compound_id}/`;
        let request_url = BASE_API_URL + url_extension;
        try {
            const response = await fetch(request_url, {
                method: "PUT",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(post_data),
            });
            if (!response.ok) {
                handle_bad_response(response);
                return false;
            }
            let data = await response.json();

            let idToFind = data.data.id;
            await get_compound_water_meters();

            let index = $all_compound_water_meters.findIndex(
                (device_config) => device_config.id == idToFind,
            );

            $selected_device = $all_compound_water_meters[index];

            close_modal();
            $toast_message = " Verbundzähler erfolgreich erstellt";

            return true;
        } catch (error) {
            return false;
        }
    }

    // delete request to the API
    async function delete_device() {
        timeout_button();

        let compound_id = $selected_device.id;
        let url_extension = `/compound_water_meters/${compound_id}`;
        let request_url = BASE_API_URL + url_extension;
        try {
            const response = await fetch(request_url, {
                method: "DELETE",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.status === 204) {
                handle_bad_response(response);
                return false;
            }

            close_modal();
            get_free_water_meters();
            $toast_message = " Gerät erfolgreich gelöscht";

            let idToFind = compound_id;
            let index = $all_compound_water_meters.findIndex(
                (compound) => compound.id == idToFind,
            );
            if (index !== -1) {
                $all_compound_water_meters.splice(index, 1);
            }
            $all_compound_water_meters = $all_compound_water_meters;
            $selected_device = null;

            return true;
        } catch (error) {
            return false;
        }
    }

    let open_dropdown_id = null; // used to open the dropdown for the water meter selection

    function handle_new_dropdown(dropdown_id) {
        device_search_input = ""; // reset the search input
        if (open_dropdown_id === dropdown_id) {
            open_dropdown_id = null;
            return;
        }
        open_dropdown_id = dropdown_id;
        document.body.addEventListener("click", (event) => {
            if (
                dropdown_menu_ref &&
                !dropdown_menu_ref.contains(event.target)
            ) {
                open_dropdown_id = null; // close the dropdown if clicked outside
            }
        });
    }

    async function set_coords() {
        // Zusammengesetzte Addresse aufbauen
        let ort = city ? city : "";
        let str = strasse ? strasse : "";
        let nmr = hausnummer ? hausnummer : "";

        var full_address = ort + " " + str + " " + nmr;

        // Koordinaten von Nominatim abfragen
        var coords = await get_coords(full_address);

        // Koordniaten in vorgesehene Felder schreiben, damit sie beim Speichern in die DB übernommen werden
        if (coords.length !== 0) {
            latitude = coords[0];
            longitude = coords[1];
        }
        // Sollten keine Koordinaten geliefert werden --> Alarm ausgeben
        else {
            alert("Addresse konnte nicht gefunden werden");
        }
    }

    function select_water_meter(device) {
        selected_water_meter = device;
        device_was_chosen = true;
        handle_new_dropdown(null);
    }

    function select_manufacturer(manufacturer) {
        selected_manufacturer = manufacturer;
    }

    function select_meter_type(meter_type) {
        selected_meter_type = meter_type;
    }

    function handle_multiple_selection(water_meter) {
        if (selected_compounds_multiple.includes(water_meter)) {
            let temp = selected_compounds_multiple.filter(
                (item) => item !== water_meter,
            );
            selected_compounds_multiple = temp;
        } else {
            selected_compounds_multiple = [
                ...selected_compounds_multiple,
                water_meter,
            ];
        }
    }
    let form;

    let name_valid = false,
        lobaro_key_valid = false,
        water_meter_id_valid = false,
        tried_submitting = false;

    // form submit function
    async function handle_submit(event) {
        event.preventDefault(); // prevent autosubmit with bad data
        if (form.checkValidity() && name_valid) {
            if ($modal_mode == "edit") {
                update_device();
            } else {
                if (compound_mode) {
                    if (!creation_main_compound) {
                        toast_message_error.set(
                            " Bitte wählen Sie einen Hauptzähler aus.",
                        );
                        return;
                    }
                    if (selected_compounds_multiple.length < 2) {
                        toast_message_error.set(
                            " Bitte wählen Sie mindestens 2 Zähler aus.",
                        );
                        return;
                    }
                    update_compound_water_meter();
                } else {
                    if (!device_was_chosen) {
                        toast_message_error.set(
                            " Bitte wählen Sie ein Gerät aus.",
                        );
                        return;
                    }
                    if (
                        !selected_water_meter &&
                        $modal_mode == "create" &&
                        (!selected_manufacturer || !selected_meter_type)
                    ) {
                        toast_message_error.set(
                            " Bitte wählen Sie einen Gerätetypen und Hersteller aus.",
                        );
                        return;
                    }
                    save_device();
                }
            }
            tried_submitting = false;
        } else {
            tried_submitting = true;
        }
    }

    function validate_name(device_name) {
        return device_name && device_name.trim().length > 0;
    }

    function validate_lobaro_key(lobaro_key) {
        return lobaro_key && (lobaro_key.trim().length === 8 || lobaro_key.trim().length === 16 || lobaro_key.trim().length === 32);
    }
    function validate_water_meter_id(water_meter_id_input) {
        return water_meter_id_input && (water_meter_id_input.trim().length === 8 || water_meter_id_input.trim().length === 11);
    }

    // Reactive validation of inputs
    $: name_valid = validate_name(device_name);
    $: lobaro_key_valid = validate_lobaro_key(lobaro_key);
    $: water_meter_id_valid = validate_water_meter_id(water_meter_id_input);
    $: if (
        compound_mode ||
        $modal_mode == "edit" ||
        selected_water_meter != null
    ) {
        lobaro_key_valid = true;
        water_meter_id_valid = true;
    } else {
        lobaro_key_valid = validate_lobaro_key(lobaro_key);
        water_meter_id_valid = validate_water_meter_id(water_meter_id_input);
    }

    $: if (!name_valid || !lobaro_key_valid || !water_meter_id_valid) {
        is_button_disabled = true;
    } else {
        is_button_disabled = false;
    }
    let device_search_input = "";
    let dropdown_menu_ref;
    let device_was_chosen = false;
</script>

{#if is_open}
    <div
        class="svelte-modal svelte-backdrop"
        tabindex="-1"
        role="dialog"
        on:mousedown|self={() => close_modal()}
        on:keydown={(e) => {
            if (e.key === "Escape") {
                close_modal();
            }
        }}
        transition:fade={{ duration: 150 }}
    >
        <div
            class="modal-dialog modal-dialog-centered modal-dialog-wide"
            role="document"
            in:fly={{ y: -400, duration: 350, delay: 100 }}
            out:fly={{ y: -400, duration: 350 }}
        >
            <div class="modal-content">
                <form bind:this={form} on:submit={handle_submit} novalidate>
                    <div class="modal-header">
                        <h5 class="modal-title" id="deleteTilte">
                            {title}
                        </h5>
                        <button
                            type="button"
                            class="close"
                            on:click={() => close_modal()}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <!-- Modal Body -->
                    <div class="modal-body" id="edit">
                        {#if $modal_mode == "create"}
                            <SegmentControl
                                bind:toggle_variable={compound_mode}
                                toggle_label_0="Einzelzähler"
                                toggle_label_1="Verbundzähler"
                            />
                        {/if}
                        <div class="mb-0 mt-2">
                            {#if $modal_mode == "create"}
                                <div class="input-group mb-3 mt-3">
                                    <label for="geraete_id">Geräteauswahl</label
                                    >
                                    {#if compound_mode}
                                        <span class="tooltip-container ml-1">
                                            <i
                                                class="fa-solid fa-circle-info fa-xs"
                                                style="color:#009FDF;"
                                            ></i>
                                            <div class="tooltip">
                                                Zur Auswahl stehen bereits
                                                angelegte Zähler, die nicht Teil
                                                eines Verbundzählers sind. Um
                                                solche Zähler erneut zuzuweisen,
                                                kann der dazugehörige
                                                Verbundzähler im "Bearbeiten"
                                                Menü gelöscht werden.
                                            </div>
                                        </span>
                                    {/if}
                                    <button
                                        class="btn btn-normal btn-transparentBlack btn-block dropdown-toggle"
                                        id="geraete_id"
                                        type="button"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                        disabled={compound_mode && $all_compound_water_meters.filter(
                                            (compound) =>
                                                compound.watermeterconfigs
                                                    .length == 1,
                                        ).length == 0}
                                        on:click|stopPropagation={() => {
                                            handle_new_dropdown("geraete_id");
                                        }}
                                    >
                                        {#if compound_mode}
                                            {#if $all_compound_water_meters.filter((compound) => compound.watermeterconfigs.length == 1).length > 0}
                                                {selected_compounds_multiple.length >
                                                0
                                                    ? selected_compounds_multiple.length ==
                                                      1
                                                        ? "1 Zähler ausgewählt"
                                                        : selected_compounds_multiple.length +
                                                          " Zähler ausgewählt"
                                                    : "Zähler wählen"}
                                            {:else}
                                                keine Zähler verfügbar
                                            {/if}
                                        {:else}
                                            {selected_water_meter
                                                ? selected_water_meter.alt_id
                                                : (device_was_chosen ? "Neues Gerät" : "Gerät wählen")}
                                        {/if}
                                    </button>

                                    <!-- Sensor Dropdown -->
                                    <div
                                        class="dropdown-menu filter_dropdown filter_dropdown_scrollable {open_dropdown_id ===
                                        'geraete_id'
                                            ? 'show'
                                            : ''}"
                                        aria-labelledby="geraete_id"
                                        bind:this={dropdown_menu_ref}
                                    >
                                        <!-- Search Bar -->
                                        <div
                                            class="input-group filter_dropdown__search"
                                        >
                                            <div class="input-group-prepend">
                                                <span
                                                    class="input-group-text"
                                                    style="background:#fff;"
                                                >
                                                    <i
                                                        class="fa fa-search"
                                                        style="color:black;"
                                                    ></i>
                                                </span>
                                            </div>
                                            <input
                                                bind:value={device_search_input}
                                                id="device_search_input"
                                                type="text"
                                                placeholder="Suche"
                                                aria-label="Search"
                                                maxlength="32"
                                                autocomplete="off"
                                                style="border-radius: 0;z-index:10000000;background:#fff;color:#495057;outline:none;flex: 1 1 auto;width:1%;"
                                            />

                                            <div
                                                class="input-group-append"
                                                style="border-top-right-radius: 0.2rem;border-bottom-right-radius: 0.2rem;background:#fff;"
                                            >
                                                <button
                                                    class="btn btn-outline-secondary delete-btn"
                                                    style="border-color:transparent;"
                                                    type="button"
                                                    on:click={() =>
                                                        (device_search_input =
                                                            "")}
                                                >
                                                    <i
                                                        class="fa fa-times"
                                                        style="color:black;"
                                                    ></i>
                                                </button>
                                            </div>
                                        </div>
                                        {#if !compound_mode}
                                            <div class="dropdown-divider"></div>
                                            <button
                                                type="button"
                                                class="dropdown-item dropdown-item-new"
                                                value="null"
                                                on:click={() =>
                                                    select_water_meter(null)}
                                                >Neues Gerät</button
                                            >
                                            <div class="dropdown-divider"></div>
                                            {#each $free_water_meters.sort((a, b) => parseInt(a.alt_id) - parseInt(b.alt_id)) as water_meter}
                                                {#if water_meter.watermeterconfig.length == 0 && water_meter.alt_id.includes(device_search_input.trim())}
                                                    <button
                                                        type="button"
                                                        class="dropdown-item dropdown-item-new"
                                                        value={water_meter.alt_id}
                                                        on:click={() =>
                                                            select_water_meter(
                                                                water_meter,
                                                            )}
                                                    >
                                                        {water_meter.alt_id}</button
                                                    >
                                                {/if}
                                            {/each}
                                        {:else if compound_mode}
                                            {#each $all_compound_water_meters as compound_water_meter}
                                                {#if compound_water_meter.watermeterconfigs.length == 1 && (compound_water_meter.watermeterconfigs[0].alt_id
                                                        .toLowerCase()
                                                        .includes(device_search_input
                                                                .trim()
                                                                .toLowerCase()) || compound_water_meter.watermeterconfigs[0].name
                                                            .toLowerCase()
                                                            .includes(device_search_input
                                                                    .trim()
                                                                    .toLowerCase()))}
                                                    <label
                                                        on:keydown={() => {}}
                                                        on:click|stopPropagation
                                                        class="dropdown__checkbox dropdown__label"
                                                    >
                                                        <input
                                                            on:click|stopPropagation={() =>
                                                                handle_multiple_selection(
                                                                    compound_water_meter,
                                                                )}
                                                            type="checkbox"
                                                            class="checkbox_test"
                                                            checked={selected_compounds_multiple.includes(
                                                                compound_water_meter,
                                                            )}
                                                        />
                                                        {compound_water_meter
                                                            .watermeterconfigs[0]
                                                            .alt_id} - {compound_water_meter
                                                            .watermeterconfigs[0]
                                                            .name}</label
                                                    >
                                                {/if}
                                            {/each}
                                        {/if}
                                    </div>
                                </div>
                            {:else}
                                <h2
                                    class="p-h2 mb-2"
                                    style="color:#009FDF; margin: 0 auto; text-align: center; display: block; width: fit-content;"
                                >
                                    {device_name}
                                </h2>
                            {/if}
                            {#if $modal_mode == "create" && !compound_mode && !selected_water_meter}
                                <div class="form-row">
                                    <div class="form-group col-md-6">
                                        <label for="manufacturer_dropwdown"
                                            >Hersteller</label
                                        >

                                        <button
                                            class="btn btn-normal btn-transparentBlack btn-block dropdown-toggle"
                                            id="manufacturer_dropwdown"
                                            type="button"
                                            aria-haspopup="true"
                                            aria-expanded="false"
                                            on:click|stopPropagation={() => {
                                                handle_new_dropdown(
                                                    "manufacturer_dropwdown",
                                                );
                                            }}
                                        >
                                            {selected_manufacturer
                                                ? selected_manufacturer.name
                                                : "Hersteller wählen"}
                                        </button>

                                        <!-- Manufacturer Dropdown -->
                                        <div
                                            class="dropdown-menu filter_dropdown mt-2 {open_dropdown_id ===
                                            'manufacturer_dropwdown'
                                                ? 'show'
                                                : ''}"
                                            aria-labelledby="manufacturer_dropwdown"
                                        >
                                            <div
                                                class="filter_dropdown_scrollable"
                                                id="manufacturer_list"
                                            >
                                                {#each MANUFACTURERS as manufacturer}
                                                    <button
                                                        type="button"
                                                        class="btn btn-transparent btn-block dropdown__checkbox"
                                                        value={manufacturer.id}
                                                        on:click={() =>
                                                            select_manufacturer(
                                                                manufacturer,
                                                            )}
                                                    >
                                                        {manufacturer.name}</button
                                                    >
                                                {/each}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group col-md-6">
                                        <label for="meter_type_dropdown"
                                            >Gerätetyp</label
                                        >
                                        <button
                                            class="btn btn-normal btn-transparentBlack btn-block dropdown-toggle"
                                            id="meter_type_dropdown"
                                            type="button"
                                            aria-haspopup="true"
                                            aria-expanded="false"
                                            on:click|stopPropagation={() => {
                                                handle_new_dropdown(
                                                    "meter_type_dropdown",
                                                );
                                            }}
                                        >
                                            {selected_meter_type
                                                ? selected_meter_type.name
                                                : "Gerätetyp wählen"}
                                        </button>

                                        <!-- Meter Type Dropdown -->
                                        <div
                                            class="dropdown-menu filter_dropdown mt-2 {open_dropdown_id ===
                                            'meter_type_dropdown'
                                                ? 'show'
                                                : ''}"
                                            aria-labelledby="meter_type_dropdown"
                                        >
                                            <div
                                                class="filter_dropdown_scrollable"
                                                id="meter_type_list"
                                            >
                                                {#each METER_TYPES as meter_type}
                                                    <button
                                                        type="button"
                                                        class="btn btn-transparent btn-block dropdown__checkbox"
                                                        value={meter_type.id}
                                                        on:click={() =>
                                                            select_meter_type(
                                                                meter_type,
                                                            )}
                                                    >
                                                        {meter_type.name}</button
                                                    >
                                                {/each}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            {/if}

                            <form>
                                {#if $modal_mode == "create" && !compound_mode && !selected_water_meter}
                                    <ModalInputField
                                        field_data={lobaro_key_field}
                                        bind:input_value={lobaro_key}
                                        bind:input_valid={lobaro_key_valid}
                                        bind:was_submitted={tried_submitting}
                                    />
                                    <ModalInputField
                                        field_data={water_meter_id_input_field}
                                        bind:input_value={water_meter_id_input}
                                        bind:input_valid={water_meter_id_valid}
                                        bind:was_submitted={tried_submitting}
                                    />
                                {/if}
                                <ModalInputField
                                    field_data={device_name_field}
                                    bind:input_value={device_name}
                                    bind:input_valid={name_valid}
                                    bind:was_submitted={tried_submitting}
                                />
                                <div class="form-row">
                                    <ModalInputField
                                        field_data={city_field}
                                        bind:input_value={city}
                                        col_class="col-md-6"
                                    />
                                    <ModalInputField
                                        field_data={ortsteil_field}
                                        bind:input_value={ortsteil}
                                        col_class="col-md-6"
                                    />
                                </div>
                                <div class="form-row">
                                    <ModalInputField
                                        field_data={strasse_field}
                                        bind:input_value={strasse}
                                        col_class="col-md-9"
                                    />
                                    <ModalInputField
                                        field_data={hausnummer_field}
                                        bind:input_value={hausnummer}
                                        col_class="col-md-3"
                                    />
                                </div>
                                <ModalInputField
                                    field_data={meter_offset_field}
                                    bind:input_value={meter_offset}
                                />

                                <div class="form-group">
                                    <label for="comment_field">Ergänzung</label>
                                    <textarea
                                        class="form-control"
                                        id="comment_field"
                                        rows="2"
                                        maxlength="256"
                                        bind:value={ergaenzung}
                                        placeholder="Ergänzung..."
                                    ></textarea>
                                </div>
                                <div class="form-group">
                                    <label for="comment_location_field"
                                        >Bemerkung zum Einbauort</label
                                    >
                                    <textarea
                                        class="form-control"
                                        id="comment_location_field"
                                        rows="2"
                                        maxlength="256"
                                        bind:value={bemerkung_lage}
                                        placeholder="Bemerkung zum Einbauort..."
                                    ></textarea>
                                </div>

                                <ToggleButton
                                    id="aktiv"
                                    label="Aktiv"
                                    bind:toggle={device_active}
                                    classes={"mt-2"}
                                />
                                <KoordinatenSetzen
                                    classes={"mt-2"}
                                    bind:longitude
                                    bind:latitude
                                    bind:setManCoords
                                    {set_coords}
                                />
                                {#if compound_mode && $modal_mode == "create"}
                                    <CompoundRoleSelection
                                        bind:compound_list={
                                            selected_compounds_multiple
                                        }
                                        bind:main_compound={
                                            creation_main_compound
                                        }
                                    />
                                {/if}
                            </form>
                        </div>
                    </div>
                    <!-- Modal Footer -->
                    <div class="modal-footer mt-2">
                        {#if $modal_mode == "edit"}
                            <button
                                type="button"
                                class="btn btn-danger mr-auto"
                                disabled={is_button_disabled}
                                on:click={() => (confirm_open = true)}
                                >Löschen</button
                            >
                        {/if}
                        <button
                            type="button"
                            class="btn btn-secondary"
                            on:click={() => close_modal()}>Abbrechen</button
                        >

                        <button
                            type="submit"
                            class="btn btn-primary"
                            disabled={is_button_disabled}>Speichern</button
                        >
                    </div>
                </form>
            </div>
        </div>
    </div>
    <ConfirmModal
        bind:show_modal={confirm_open}
        title={confirm_title}
        on:confirm={() => delete_device()}
    />
{/if}
